/**
 * Loader geometry — turns the Bromo brand shapes into morph-ready point rings.
 *
 * The brand shapes (see views/components/atoms/shapes/shape-defs.twig, the
 * canonical clip-path source) each have a *different* command structure, so
 * unlike the two hand-topology-matched hero shapes (hero-shapes.js) they can't be
 * lerped anchor-for-anchor as authored. Instead each path is:
 *
 *   1. parsed into cubic segments (parsePath),
 *   2. resampled to a fixed count of equal-arc-length points (resample) — so
 *      point i sits at the same perimeter fraction on every shape, and
 *   3. rotation/winding-aligned to a common reference (align) so consecutive
 *      morphs travel the shortest way round instead of twisting.
 *
 * The result is a set of equal-length flat rings [x,y, x,y, …] that interpolate
 * cleanly with a plain element-wise lerp. buildPath renders one as a closed
 * polyline; at SAMPLES resolution the straight segments read as smooth curves.
 *
 * All coordinates stay in objectBoundingBox space (0→1), matching the source
 * clip paths, so the loader mark is resolution-independent.
 */

// Verbatim from shape-defs.twig. If the brand paths change there, update here too.
export const BRAND_PATHS = {
  arch:      'M0 0.96883V0.5C0 0.22382 0.22389 0 0.49995 0C0.77602 0 1 0.22391 1 0.5V0.96883C1 0.98603 0.98603 1 0.96874 1H0.03117C0.01397 1 0 0.98603 0 0.96883Z',
  tab:       'M0.5267 0.19338V0.02547C0.5267 0.01118 0.51482-0.00064 0.50067 0.00003C0.22187 0.01289 0 0.23179 0 0.49995C0 0.76812 0.22187 0.98701 0.50067 0.99988C0.51482 1.00055 0.5267 0.98863 0.5267 0.97443V0.80652C0.5267 0.78241 0.55711 0.77212 0.57164 0.79137C0.66248 0.91173 0.80815 0.99225 0.97396 0.99997C0.98812 1.00064 1 0.98882 1 0.97453V0.02557C1 0.01137 0.98822-0.00055 0.97396 0.00012C0.80815 0.00775 0.66248 0.08827 0.57164 0.20873C0.55711 0.22798 0.5267 0.21768 0.5267 0.19357',
  hourglass: 'M1 0.53122V0.96883C1 0.98603 0.98603 1 0.96883 1H0.03117C0.01397 1 0 0.98603 0 0.96883V0.53122C0 0.51402 0.01397 0.50005 0.03117 0.50005H0.21887C0.25252 0.50005 0.2663 0.4568 0.23883 0.43732L0.21393 0.41973C0.09409 0.33619 0.01074 0.1997 0.00095 0.03298C0 0.01511 0.01426 0 0.03231 0H0.96778C0.98565 0 1.00009 0.01511 0.99896 0.03298C0.99107 0.16776 0.93499 0.28305 0.85022 0.36641C0.83054 0.3858 0.80907 0.40358 0.78645 0.41935C0.77514 0.42734 0.76602 0.43703 0.75917 0.44777C0.74482 0.47039 0.76259 0.49995 0.78939 0.49995H0.96883C0.98603 0.49995 1 0.51392 1 0.53113',
  church:    'M0.99997 0.02607C0.99227 0.19297 0.91177 0.33953 0.7914 0.431C0.77218 0.44561 0.78246 0.47617 0.80663 0.47617H0.97457C0.98665 0.47617 0.99816 0.48457 0.9994 0.49612C0.99978 0.49956 0.9994 0.50739 0.9994 0.51006V0.73874C0.9994 0.87604 0.89397 0.99577 0.75724 0.99988C0.72042 1.00103 0.68531 0.99405 0.65353 0.98078C0.62146 0.96732 0.59273 0.94746 0.56913 0.92263C0.53107 0.88272 0.46846 0.88272 0.4304 0.92263C0.4068 0.94736 0.37816 0.96732 0.34619 0.98069C0.31431 0.99405 0.2791 1.00103 0.24219 0.99988C0.10545 0.99577 0.00003 0.87604 0.00003 0.73874V0.50242C0.00003 0.5008 0.00041 0.49774 0.0006 0.49622C0.0025 0.48476 0.01335 0.47626 0.02543 0.47626H0.19337C0.21754 0.47626 0.22782 0.44571 0.2086 0.4311C0.08823 0.33963 0.00764 0.19306 0.00003 0.02616C-0.00064 0.01184 0.01125 0 0.02543 0H0.97457C0.98884 0 1.00064 0.01184 0.99997 0.02616',
  split:     'M0.92673 0.07525C0.8815 0.03002 0.81906 0 0.75007 0C0.63604 0 0.57284 0.06071 0.53853 0.11867C0.5278 0.13682 0.49995 0.12893 0.49995 0.10793V0.03135C0.49995 0.01416 0.48598 0.00019 0.46878 0.00019H0.03117C0.01397 0.00019 0 0.01416 0 0.03135V0.75012C0 0.81919 0.02803 0.87962 0.07327 0.92475C0.1185 0.96998 0.18094 1 0.25002 1C0.36406 1 0.42725 0.93929 0.46156 0.88133C0.4723 0.86318 0.50005 0.87107 0.50005 0.89216V0.96874C0.50005 0.98594 0.51402 0.99991 0.53122 0.99991H0.96883C0.98603 0.99991 1 0.98594 1 0.96874V0.24998C1 0.1809 0.97197 0.12057 0.92673 0.07534',
  bowtie:    'M0.99981 0.96832V0.03176C0.99981 0.01386 0.98508-0.001 0.96722 0.00005C0.83219 0.00786 0.71684 0.06403 0.6335 0.14905C0.6241 0.15867 0.61507 0.16857 0.60652 0.17895C0.54789 0.24931 0.45344 0.25188 0.39519 0.18114C0.31062 0.07841 0.18396 0.00881 0.0324 0.00005C0.01463-0.001 0 0.01376 0 0.03166V0.96823C0 0.98613 0.01473 1.00098 0.03259 0.99993C0.16752 0.99213 0.28288 0.93586 0.3663 0.85093C0.37571 0.84132 0.38474 0.83142 0.39329 0.82113C0.45192 0.75077 0.54646 0.7482 0.60471 0.81894C0.689 0.92119 0.8149 0.9907 0.9657 0.99993C0.98451 1.00107 1 0.98737 1 0.96842',
  scallop:   'M1 0.75009C1 0.6653 0.96534 0.6089 0.92191 0.57165C0.87963 0.53535 0.88163 0.4698 0.9221 0.4314C0.9701 0.38586 1 0.32145 1 0.2501C1 0.11214 0.88839 0.00038 0.7507 0H0.26037C0.12353 0 0.00421 0.10547 0.00011 0.24228C-0.00208 0.31641 0.02792 0.38348 0.07715 0.43064C0.11686 0.46875 0.11686 0.53144 0.07715 0.56946C0.02792 0.61662-0.00208 0.68369 0.00011 0.75772C0.00421 0.89453 0.12353 1 0.26037 1H0.7507C0.88839 0.99962 1 0.88786 1 0.75',
  flower:    'M0.91361 0.08679C0.80566-0.02149 0.63498-0.0284 0.5189 0.06578C0.50752 0.07499 0.49136 0.07508 0.47989 0.06588C0.3638-0.02725 0.19408-0.02006 0.08651 0.08784C-0.02087 0.19554-0.02823 0.3654 0.06413 0.48183C0.07312 0.49315 0.07322 0.50916 0.06423 0.52058C-0.02699 0.63701-0.01934 0.8059 0.08765 0.91322C0.19532 1.02121 0.36505 1.02831 0.48113 0.93509C0.49251 0.92597 0.50867 0.92597 0.52004 0.93509C0.63613 1.02831 0.80595 1.02112 0.91351 0.91322C1.0209 0.80552 1.02816 0.63585 0.93598 0.51943C0.9269 0.50801 0.9269 0.492 0.93598 0.48058C1.02816 0.36415 1.0209 0.19439 0.91351 0.08679',
};

// Points sampled per shape. High enough that the straight-segment ring reads as
// a smooth blob even at large sizes; the per-frame lerp cost stays trivial.
const SAMPLES = 100;

// Substeps each cubic is flattened into before arc-length resampling.
const FLATTEN_STEPS = 20;

/**
 * Parse an absolute/relative SVG path into a flat list of cubic segments
 * [x0,y0, c1x,c1y, c2x,c2y, x1,y1]. Handles M, L, H, V, C, Z (lines become
 * degenerate cubics with colinear controls). The path is always closed.
 */
function parsePath(d) {
  const tokens = d.match(/([MLHVCSQTAZmlhvcsqtaz])|(-?\d*\.?\d+)/g) || [];
  const segs = [];
  let i = 0;
  let cmd = '';
  let cx = 0, cy = 0, sx = 0, sy = 0;
  const num = () => parseFloat(tokens[i++]);
  const isCmd = (t) => /^[MLHVCSQTAZmlhvcsqtaz]$/.test(t);
  const lineTo = (x, y) => { segs.push([cx, cy, cx, cy, x, y, x, y]); cx = x; cy = y; };

  while (i < tokens.length) {
    if (isCmd(tokens[i])) { cmd = tokens[i]; i++; }
    const rel = cmd === cmd.toLowerCase();
    switch (cmd.toUpperCase()) {
      case 'M': {
        let x = num(), y = num();
        if (rel) { x += cx; y += cy; }
        cx = x; cy = y; sx = x; sy = y;
        cmd = rel ? 'l' : 'L'; // extra coordinate pairs after M are implicit L
        break;
      }
      case 'L': { let x = num(), y = num(); if (rel) { x += cx; y += cy; } lineTo(x, y); break; }
      case 'H': { let x = num(); if (rel) x += cx; lineTo(x, cy); break; }
      case 'V': { let y = num(); if (rel) y += cy; lineTo(cx, y); break; }
      case 'C': {
        let c1x = num(), c1y = num(), c2x = num(), c2y = num(), x = num(), y = num();
        if (rel) { c1x += cx; c1y += cy; c2x += cx; c2y += cy; x += cx; y += cy; }
        segs.push([cx, cy, c1x, c1y, c2x, c2y, x, y]);
        cx = x; cy = y;
        break;
      }
      case 'Z': { if (cx !== sx || cy !== sy) lineTo(sx, sy); cx = sx; cy = sy; break; }
      default: i++; // unsupported command token — skip so we never spin
    }
  }
  // Close any open subpath so the ring is continuous for sampling.
  if (segs.length && (cx !== sx || cy !== sy)) segs.push([cx, cy, cx, cy, sx, sy, sx, sy]);
  return segs;
}

/** Push FLATTEN_STEPS points along one cubic (excluding its start) into `out`. */
function flattenCubic(seg, out) {
  const [x0, y0, c1x, c1y, c2x, c2y, x1, y1] = seg;
  for (let s = 1; s <= FLATTEN_STEPS; s++) {
    const t = s / FLATTEN_STEPS, u = 1 - t;
    const a = u * u * u, b = 3 * u * u * t, c = 3 * u * t * t, e = t * t * t;
    out.push(a * x0 + b * c1x + c * c2x + e * x1, a * y0 + b * c1y + c * c2y + e * y1);
  }
}

/** Resample a cubic segment list into a closed ring of K equal-arc-length points. */
function resample(segs, K) {
  const dense = [segs[0][0], segs[0][1]];
  for (const seg of segs) flattenCubic(seg, dense);

  const n = dense.length / 2;
  const cum = [0];
  for (let i = 1; i < n; i++) {
    const dx = dense[2 * i] - dense[2 * i - 2];
    const dy = dense[2 * i + 1] - dense[2 * i - 1];
    cum.push(cum[i - 1] + Math.hypot(dx, dy));
  }
  const total = cum[n - 1] || 1;

  const out = new Array(2 * K);
  let j = 0;
  for (let k = 0; k < K; k++) {
    const target = (k / K) * total; // [0, total) — closing vertex not duplicated
    while (j < n - 1 && cum[j + 1] < target) j++;
    const span = cum[j + 1] - cum[j] || 1;
    const f = (target - cum[j]) / span;
    out[2 * k] = dense[2 * j] + (dense[2 * j + 2] - dense[2 * j]) * f;
    out[2 * k + 1] = dense[2 * j + 1] + (dense[2 * j + 3] - dense[2 * j + 1]) * f;
  }
  return out;
}

/**
 * Reorder a ring (rotation + winding direction) to best match `ref`, minimising
 * the summed squared point distances. Keeps corresponding features paired so the
 * morph moves the short way instead of spiralling.
 */
function align(ref, cand) {
  const K = ref.length / 2;
  const at = (dir, r, i) => ((dir === 1 ? i + r : r - i) % K + K) % K;
  let best = { cost: Infinity, r: 0, dir: 1 };

  for (const dir of [1, -1]) {
    for (let r = 0; r < K; r++) {
      let cost = 0;
      for (let i = 0; i < K; i++) {
        const idx = at(dir, r, i);
        const dx = ref[2 * i] - cand[2 * idx];
        const dy = ref[2 * i + 1] - cand[2 * idx + 1];
        cost += dx * dx + dy * dy;
        if (cost >= best.cost) break; // early out — can't beat the current best
      }
      if (cost < best.cost) best = { cost, r, dir };
    }
  }

  const out = new Array(2 * K);
  for (let i = 0; i < K; i++) {
    const idx = at(best.dir, best.r, i);
    out[2 * i] = cand[2 * idx];
    out[2 * i + 1] = cand[2 * idx + 1];
  }
  return out;
}

let cache = null;

/**
 * The aligned brand rings, in morph order (memoised — computed once, shared by
 * every loader instance). Order is arbitrary per the brief; the signature
 * scallop leads and doubles as the reference + reduced-motion resting shape.
 */
export function getLoaderGeometry() {
  if (cache) return cache;
  const order = ['scallop', 'flower', 'church', 'bowtie', 'hourglass', 'split', 'tab', 'arch'];
  const rings = order.map((name) => resample(parsePath(BRAND_PATHS[name]), SAMPLES));
  const ref = rings[0];
  cache = rings.map((ring, i) => (i === 0 ? ring : align(ref, ring)));
  return cache;
}

const round = (n) => Math.round(n * 1e4) / 1e4;

/** Render a flat point ring as a closed SVG path (M + L… + Z). */
export function buildPath(ring) {
  let d = `M${round(ring[0])} ${round(ring[1])}`;
  for (let i = 2; i < ring.length; i += 2) d += `L${round(ring[i])} ${round(ring[i + 1])}`;
  return `${d}Z`;
}

/** Element-wise linear interpolation of two equal-length point rings. */
export function lerpPoints(a, b, t) {
  const out = new Array(a.length);
  for (let i = 0; i < a.length; i++) out[i] = a[i] + (b[i] - a[i]) * t;
  return out;
}
